import type { LiveActionResponse } from '../types';
import {
  actionWindowProgress,
  deriveActionStream,
  secondsUntilAction,
} from '../lib/deriveActionStream';
import { formatBuyType } from '../lib/formatters';
import { formatClock } from '../lib/roundClock';
import styles from './ActionStream.module.css';

export type ActionStreamProps = {
  actions: LiveActionResponse[];
  currentClockSecs: number | null;
  strategyName?: string | null;
  side?: string | null;
  buyType?: string | null;
  waiting?: boolean;
};

export default function ActionStream({
  actions,
  currentClockSecs,
  strategyName,
  side,
  buyType,
  waiting = false,
}: ActionStreamProps) {
  const clock = currentClockSecs ?? 0;
  const stream = deriveActionStream(actions, clock);
  const sideNorm = (side ?? '').toUpperCase();
  const sideTagClass = sideNorm === 'CT' ? styles.tagCt : sideNorm === 'T' ? styles.tagT : '';

  return (
    <div className={styles.main}>
      <div className={styles.ticker}>
        <div className={styles.tickerLeft}>
          <div className={styles.strat}>
            <span className={styles.label}>{waiting ? 'Waiting' : 'Running'}</span>
            {strategyName || (waiting ? 'No active round' : '—')}
          </div>
          {(side || buyType) && (
            <div className={styles.tags}>
              {side && <span className={`${styles.tag} ${sideTagClass}`}>{sideNorm || side}</span>}
              {buyType && <span className={styles.tag}>{formatBuyType(buyType)}</span>}
            </div>
          )}
        </div>
        <div className={styles.clock}>
          <span className={styles.clockLbl}>Round time</span>
          {currentClockSecs == null ? '—' : formatClock(currentClockSecs)}
        </div>
      </div>

      <div className={styles.streamWrap}>
        {waiting ? (
          <p className={styles.empty}>Waiting for the round to start…</p>
        ) : actions.length === 0 ? (
          <p className={styles.empty}>No actions for your role this round.</p>
        ) : (
          <div className={styles.stream}>
            {stream.done.map((a) => (
              <div key={a.id} className={styles.action}>
                <div className={styles.doneInner}>
                  <span className={styles.check} aria-hidden="true">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12l5 5L20 7" />
                    </svg>
                  </span>
                  <span className={styles.doneText}>{a.description || a.actionType}</span>
                  <span className={styles.time}>{formatClock(a.roundTimeSecs)}</span>
                </div>
              </div>
            ))}

            {stream.next && (
              <>
                <div className={styles.sectionLabel}>Up Next</div>
                <NextActionCard action={stream.next} currentClockSecs={clock} />
              </>
            )}

            {stream.upcoming.length > 0 && (
              <>
                <div className={styles.sectionLabel}>Then</div>
                {stream.upcoming.map((a) => (
                  <div key={a.id} className={styles.action}>
                    <div className={styles.upcomingInner}>
                      <span className={styles.pip} aria-hidden="true" />
                      <span className={styles.upcomingText}>{a.description || a.actionType}</span>
                      <span className={styles.hint}>{a.actionType}</span>
                      <span className={styles.upcomingTime}>{formatClock(a.roundTimeSecs)}</span>
                    </div>
                  </div>
                ))}
              </>
            )}

            {!stream.next && stream.done.length > 0 && (
              <p className={styles.empty}>All actions complete for this round.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function NextActionCard({
  action,
  currentClockSecs,
}: {
  action: LiveActionResponse;
  currentClockSecs: number;
}) {
  const inSecs = secondsUntilAction(action, currentClockSecs);
  const progress = actionWindowProgress(action, currentClockSecs);
  const utilType = action.utilityUse?.utilityType;
  const utilName = action.utilityUse?.name;

  return (
    <div className={styles.next}>
      <div className={styles.nextCard}>
        <div className={styles.nextHead}>
          <span className={styles.nextLabel}>
            <span className={styles.nextDot} /> Next Action
          </span>
          <div className={styles.nextTags}>
            {utilType && <span className={`${styles.tag} ${styles.utilTag}`}>{utilType}</span>}
            <span className={styles.tag}>{action.actionType}</span>
          </div>
        </div>
        <h2 className={styles.nextTitle}>{action.description || action.actionType}</h2>
        {utilName && (
          <p className={styles.nextSub}>
            <span className={styles.utilName}>{utilName}</span>
            {action.utilityUse?.description ? ` — ${action.utilityUse.description}` : null}
          </p>
        )}
        <div className={styles.timerRow}>
          <div className={styles.countdown}>
            <span className={styles.countdownLbl}>In</span>
            {formatClock(inSecs)}
          </div>
          {progress != null && action.durationSecs != null && (
            <div className={styles.progressOuter}>
              <div className={styles.progressLabel}>
                <span>Action window</span>
                <span>{action.durationSecs}s</span>
              </div>
              <div className={styles.progressBar} aria-hidden="true">
                <span style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
            </div>
          )}
          <div className={styles.clockAt}>
            At<span className={styles.clockAtVal}>{formatClock(action.roundTimeSecs)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
